import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { formatRunInterval, formatRunTimestamp, statusChipColor } from "./pvGenerationUtils";
import AppDatePicker from "../common/AppDatePicker";

const PAGE_SIZE = 10;

function runTypeShortLabel(run) {
  return run?.run_type === "actual_weather" ? "Historical" : "Forecast";
}

// PV run history: mirrors the Wind run history table structure exactly
// (Show · Type · Interval · Status · Triggered · Message · Actions), with the
// same filters, pagination, per-row actions, and details dialog.
export default function PvRunHistoryTable({
  runs,
  selectedRunIds,
  onToggleRun,
  seriesLoadingIds,
  onExportRun,
  onRerun,
  timeZone,
}) {
  const [sourceFilter, setSourceFilter] = useState("all");
  const [completedOnly, setCompletedOnly] = useState(false);
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [simFrom, setSimFrom] = useState("");
  const [simTo, setSimTo] = useState("");
  const [menuState, setMenuState] = useState(null); // { anchorEl, run }
  const [detailsRun, setDetailsRun] = useState(null);
  const [busyRunId, setBusyRunId] = useState(null);
  const [actionError, setActionError] = useState("");
  const [page, setPage] = useState(0);
  const [goTo, setGoTo] = useState("");

  const hasActiveFilters =
    sourceFilter !== "all" || completedOnly || createdFrom || createdTo || simFrom || simTo;

  const handleClearFilters = () => {
    setSourceFilter("all");
    setCompletedOnly(false);
    setCreatedFrom("");
    setCreatedTo("");
    setSimFrom("");
    setSimTo("");
  };

  const filteredRuns = useMemo(() => {
    return runs
      .filter((r) => sourceFilter === "all" || r.run_type === sourceFilter)
      .filter((r) => !completedOnly || r.status === "completed")
      .filter((r) => !createdFrom || (r.created_at && r.created_at.slice(0, 10) >= createdFrom))
      .filter((r) => !createdTo || (r.created_at && r.created_at.slice(0, 10) <= createdTo))
      .filter((r) => !simFrom || (r.end_date && r.end_date >= simFrom))
      .filter((r) => !simTo || (r.start_date && r.start_date <= simTo))
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [runs, sourceFilter, completedOnly, createdFrom, createdTo, simFrom, simTo]);

  const totalPages = Math.max(1, Math.ceil(filteredRuns.length / PAGE_SIZE));
  const pagedRuns = useMemo(
    () => filteredRuns.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [filteredRuns, page]
  );

  useEffect(() => {
    setPage(0);
  }, [sourceFilter, completedOnly, createdFrom, createdTo, simFrom, simTo]);

  useEffect(() => {
    if (page > totalPages - 1) setPage(totalPages - 1);
  }, [page, totalPages]);

  const runError = (run) =>
    run?.error_message || run?.error || run?.message || run?.status_message || null;

  const handleAction = async (label, run) => {
    setMenuState(null);
    setActionError("");
    if (label === "compare") {
      onToggleRun(run.run_id);
      return;
    }
    if (label === "details" || label === "error") {
      setDetailsRun(run);
      return;
    }
    setBusyRunId(run.run_id);
    try {
      if (label === "export") await onExportRun(run);
      if (label === "rerun") await onRerun(run);
    } catch (err) {
      setActionError(err?.message || "Action failed.");
    } finally {
      setBusyRunId(null);
    }
  };

  if (!runs.length) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        No runs yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Type</InputLabel>
          <Select label="Type" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
            <MenuItem value="all">All types</MenuItem>
            <MenuItem value="actual_weather">Historical only</MenuItem>
            <MenuItem value="forecast">Forecast only</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={completedOnly ? "completed" : "all"}
            onChange={(e) => setCompletedOnly(e.target.value === "completed")}
          >
            <MenuItem value="all">All statuses</MenuItem>
            <MenuItem value="completed">Completed only</MenuItem>
          </Select>
        </FormControl>
        <AppDatePicker
          label="Triggered from"
          value={createdFrom}
          onChange={setCreatedFrom}
        />
        <AppDatePicker
          label="Triggered to"
          value={createdTo}
          onChange={setCreatedTo}
        />
        <AppDatePicker
          label="Interval from"
          value={simFrom}
          onChange={setSimFrom}
        />
        <AppDatePicker
          label="Interval to"
          value={simTo}
          onChange={setSimTo}
        />
        <Button
          size="small"
          variant="text"
          startIcon={<ClearIcon fontSize="small" />}
          onClick={handleClearFilters}
          disabled={!hasActiveFilters}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Clear filters
        </Button>
      </Stack>

      {actionError && <Alert severity="error">{actionError}</Alert>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">Show</TableCell>
            <TableCell>Weather source</TableCell>
            <TableCell>Interval</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Triggered</TableCell>
            <TableCell>Message</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pagedRuns.map((run) => {
            const failed = run.status === "failed";
            return (
              <TableRow key={run.run_id} sx={failed ? { opacity: 0.6 } : undefined}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selectedRunIds.includes(run.run_id)}
                    disabled={run.status !== "completed"}
                    onChange={() => onToggleRun(run.run_id)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{runTypeShortLabel(run)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{formatRunInterval(run)}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={run.status || "unknown"}
                    color={statusChipColor(run.status)}
                    variant="filled"
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">
                      {formatRunTimestamp(run.created_at, timeZone)}
                    </Typography>
                    {seriesLoadingIds.includes(run.run_id) && <CircularProgress size={14} />}
                    {busyRunId === run.run_id && <CircularProgress size={14} />}
                  </Stack>
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Typography
                    variant="caption"
                    color={failed ? "error" : "text.secondary"}
                    noWrap
                    title={runError(run) || ""}
                    sx={{ display: "block", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {runError(run) || ""}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={(e) => setMenuState({ anchorEl: e.currentTarget, run })}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
          {pagedRuns.length === 0 && (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography variant="body2" sx={{ color: "text.secondary", py: 1 }}>
                  No runs match these filters.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {filteredRuns.length > PAGE_SIZE && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1, py: 0.5 }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Page {page + 1} of {totalPages}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <IconButton size="small" onClick={() => setPage(0)} disabled={page === 0} aria-label="First page">
              <FirstPageIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setPage((prev) => Math.max(0, prev - 1))}
              disabled={page === 0}
              aria-label="Previous page"
            >
              <KeyboardArrowLeftIcon fontSize="small" />
            </IconButton>
            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={(_, value) => setPage(value - 1)}
              size="small"
              siblingCount={1}
              boundaryCount={1}
              hidePrevButton
              hideNextButton
            />
            <IconButton
              size="small"
              onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
              disabled={page >= totalPages - 1}
              aria-label="Next page"
            >
              <KeyboardArrowRightIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setPage(totalPages - 1)}
              disabled={page >= totalPages - 1}
              aria-label="Last page"
            >
              <LastPageIcon fontSize="small" />
            </IconButton>
            <TextField
              size="small"
              type="number"
              value={goTo}
              onChange={(e) => setGoTo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                const next = Number.parseInt(goTo, 10);
                if (!Number.isFinite(next)) return;
                const clamped = Math.min(Math.max(next, 1), totalPages);
                setPage(clamped - 1);
              }}
              sx={{
                "& .MuiInputBase-root": { height: 28 },
                "& .MuiInputBase-input": { py: 0, fontSize: 12 },
              }}
              inputProps={{ min: 1, max: totalPages, style: { textAlign: "center", width: 32 } }}
            />
          </Stack>
        </Box>
      )}

      <Menu
        anchorEl={menuState?.anchorEl}
        open={Boolean(menuState)}
        onClose={() => setMenuState(null)}
      >
        {menuState?.run?.status === "failed"
          ? [
              <MenuItem key="error" onClick={() => handleAction("error", menuState.run)}>
                <ListItemText>View error</ListItemText>
              </MenuItem>,
              <MenuItem key="rerun" onClick={() => handleAction("rerun", menuState.run)}>
                <ListItemText>Rerun</ListItemText>
              </MenuItem>,
            ]
          : [
              <MenuItem
                key="compare"
                disabled={menuState?.run?.status !== "completed"}
                onClick={() => handleAction("compare", menuState.run)}
              >
                <ListItemText>Compare</ListItemText>
              </MenuItem>,
              <MenuItem key="details" onClick={() => handleAction("details", menuState.run)}>
                <ListItemText>View details</ListItemText>
              </MenuItem>,
              <MenuItem
                key="export"
                disabled={menuState?.run?.status !== "completed"}
                onClick={() => handleAction("export", menuState.run)}
              >
                <ListItemText>Export this run</ListItemText>
              </MenuItem>,
              <MenuItem key="rerun2" onClick={() => handleAction("rerun", menuState.run)}>
                <ListItemText>Rerun</ListItemText>
              </MenuItem>,
            ]}
      </Menu>

      <Dialog open={Boolean(detailsRun)} onClose={() => setDetailsRun(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Run details
          <IconButton size="small" onClick={() => setDetailsRun(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {detailsRun && (
            <Stack spacing={1}>
              <Typography variant="body2"><strong>Run ID:</strong> {detailsRun.run_id}</Typography>
              <Typography variant="body2"><strong>Type:</strong> {runTypeShortLabel(detailsRun)} weather</Typography>
              <Typography variant="body2"><strong>Triggered:</strong> {formatRunTimestamp(detailsRun.created_at, timeZone)}</Typography>
              <Typography variant="body2"><strong>Interval:</strong> {formatRunInterval(detailsRun)}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {detailsRun.status || "unknown"}</Typography>
              {runError(detailsRun) && (
                <Typography variant="body2" sx={{ color: detailsRun.status === "failed" ? "error.main" : "text.primary" }}>
                  <strong>Message:</strong> {runError(detailsRun)}
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
